import React, { useState, useEffect } from 'react';
import {
  Table, Button, Tag, Popconfirm, Space, Input, Modal, Form, Typography,
  Select, message, Divider, Card, Switch,
} from 'antd';
import { PlusOutlined, DeleteOutlined, ExclamationCircleOutlined, WarningOutlined, ApiOutlined } from '@ant-design/icons';
import api from '../api';

const { Title, Text } = Typography;

const actionLabels = { replace: { label: '替换', color: 'orange' }, block: { label: '拦截', color: 'red' } };
const categoryLabels = { general: '通用', politics: '政治', violence: '暴力', porn: '色情', ad: '广告' };

function SensitiveWordPage() {
  const [words, setWords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [bulkModal, setBulkModal] = useState(false);
  const [form] = Form.useForm();
  const [bulkForm] = Form.useForm();

  // AI 监测配置
  const [ai, setAi] = useState({ enabled: false, endpoint: '', apiKey: '', model: 'gpt-4o-mini', prompt: '' });
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSaving, setAiSaving] = useState(false);
  const [aiTestText, setAiTestText] = useState('');
  const [aiTestResult, setAiTestResult] = useState(null);
  const [aiTesting, setAiTesting] = useState(false);

  const loadWords = async (p = 1) => {
    setLoading(true);
    try {
      const { list, total: t } = await api.get('/admin/sensitive-words', { params: { page: p, limit: 50 } });
      setWords(list);
      setTotal(t);
      setPage(p);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadAI = async () => {
    setAiLoading(true);
    try {
      const cfg = await api.get('/admin/sensitive-words/ai-config');
      setAi(cfg || { enabled: false, endpoint: '', apiKey: '', model: 'gpt-4o-mini', prompt: '' });
    } catch (e) {
      console.error(e);
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => { loadWords(); loadAI(); }, []);

  const handleCreate = async () => {
    const values = await form.validateFields();
    await api.post('/admin/sensitive-words', values);
    message.success('添加成功');
    setCreateModal(false);
    form.resetFields();
    loadWords(page);
  };

  const handleBulkCreate = async () => {
    const { words: text, category, action } = bulkForm.getFieldsValue();
    if (!text) return;
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    const data = lines.map(w => ({ word: w, category, action }));
    await api.post('/admin/sensitive-words/bulk', { words: data });
    message.success(`已添加 ${data.length} 个敏感词`);
    setBulkModal(false);
    bulkForm.resetFields();
    loadWords(page);
  };

  const handleDelete = async (id) => {
    await api.delete(`/admin/sensitive-words/${id}`);
    message.success('已删除');
    loadWords(page);
  };

  const handleSaveAI = async () => {
    setAiSaving(true);
    try {
      await api.post('/admin/sensitive-words/ai-config', ai);
      message.success('AI 监测配置已保存');
    } catch (e) {
      message.error((e && e.error) || '保存失败');
    } finally {
      setAiSaving(false);
    }
  };

  const handleTestAI = async () => {
    if (!aiTestText) return message.warning('请输入测试文本');
    setAiTesting(true);
    try {
      const r = await api.post('/admin/sensitive-words/ai-test', { text: aiTestText });
      setAiTestResult(r);
    } catch (e) {
      message.error((e && e.error) || '测试失败');
    } finally {
      setAiTesting(false);
    }
  };

  const columns = [
    { title: 'ID', width: 80, dataIndex: 'id' },
    { title: '敏感词', dataIndex: 'word', width: 200, render: (v) => <Text strong style={{ color: '#ff4d4f' }}>{v}</Text> },
    {
      title: '分类', width: 100,
      render: (_, r) => <Tag color="blue">{categoryLabels[r.category] || r.category || '通用'}</Tag>,
    },
    {
      title: '处理方式', width: 100,
      render: (_, r) => <Tag color={actionLabels[r.action]?.color}>{actionLabels[r.action]?.label || r.action}</Tag>,
    },
    { title: '添加时间', dataIndex: 'created_at', width: 160, render: (v) => v ? new Date(v).toLocaleString() : '-' },
    {
      title: '操作', width: 100,
      render: (_, r) => (
        <Popconfirm title="确定删除?" onConfirm={() => handleDelete(r.id)} icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}>
          <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4}>敏感词管理</Title>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModal(true)} style={{ background: '#07C160' }}>添加敏感词</Button>
          <Button icon={<WarningOutlined />} onClick={() => setBulkModal(true)}>批量导入</Button>
        </Space>
      </div>

      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        敏感词用于内容审核。用户发布的内容如包含敏感词，将被替换为 * 或直接拦截。
      </Text>

      <Table columns={columns} dataSource={words} rowKey="id" loading={loading}
        pagination={{ current: page, total, pageSize: 50, onChange: loadWords, showSizeChanger: false }} size="small" />

      {/* AI 监测配置 */}
      <Divider />
      <Card
        title={<Space><ApiOutlined /> AI 内容监测</Space>}
        extra={!aiLoading && <Text type="secondary">{ai.enabled ? <Tag color="green">已开启</Tag> : <Tag>未开启</Tag>}</Text>}
        loading={aiLoading}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          开启后，朋友圈等公开内容发布时将调用下方 AI 接口进行增强审核（兼容 OpenAI 风格 /chat/completions，需返回 JSON 判定）。
        </Text>
        <Form layout="vertical">
          <Form.Item label="启用 AI 监测">
            <Switch checked={ai.enabled} onChange={(v) => setAi({ ...ai, enabled: v })} />
          </Form.Item>
          <Form.Item label="AI 接口地址（endpoint）">
            <Input value={ai.endpoint} onChange={(e) => setAi({ ...ai, endpoint: e.target.value })}
              placeholder="https://api.openai.com/v1/chat/completions" />
          </Form.Item>
          <Form.Item label="API Key">
            <Input.Password value={ai.apiKey} onChange={(e) => setAi({ ...ai, apiKey: e.target.value })}
              placeholder="sk-..." />
          </Form.Item>
          <Form.Item label="模型名">
            <Input value={ai.model} onChange={(e) => setAi({ ...ai, model: e.target.value })} placeholder="gpt-4o-mini" />
          </Form.Item>
          <Form.Item label="审核提示词（prompt）">
            <Input.TextArea rows={4} value={ai.prompt} onChange={(e) => setAi({ ...ai, prompt: e.target.value })} />
          </Form.Item>
          <Space>
            <Button type="primary" loading={aiSaving} onClick={handleSaveAI}>保存配置</Button>
          </Space>
        </Form>

        <Divider />
        <Title level={5}>接口测试</Title>
        <Input.TextArea rows={3} value={aiTestText} onChange={(e) => setAiTestText(e.target.value)}
          placeholder="输入一段文本测试 AI 判定结果" />
        <div style={{ marginTop: 8 }}>
          <Button loading={aiTesting} onClick={handleTestAI}>测试</Button>
        </div>
        {aiTestResult && (
          <Card size="small" style={{ marginTop: 12 }} title="返回结果">
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(aiTestResult, null, 2)}</pre>
          </Card>
        )}
      </Card>

      {/* 添加 */}
      <Modal title="添加敏感词" open={createModal} onOk={handleCreate} onCancel={() => setCreateModal(false)}>
        <Form form={form} layout="vertical">
          <Form.Item name="word" label="敏感词" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="category" label="分类" initialValue="general">
            <Select options={Object.entries(categoryLabels).map(([k, v]) => ({ value: k, label: v }))} />
          </Form.Item>
          <Form.Item name="action" label="处理方式" initialValue="replace">
            <Select options={Object.entries(actionLabels).map(([k, v]) => ({ value: k, label: v.label }))} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量导入 */}
      <Modal title="批量导入敏感词" open={bulkModal} onOk={handleBulkCreate} onCancel={() => setBulkModal(false)} width={500}>
        <Form form={bulkForm} layout="vertical">
          <Form.Item name="words" label="敏感词列表" rules={[{ required: true }]}>
            <Input.TextArea rows={10} placeholder="每行一个敏感词：&#10;反动&#10;暴力&#10;色情" />
          </Form.Item>
          <Form.Item name="category" label="分类" initialValue="general">
            <Select options={Object.entries(categoryLabels).map(([k, v]) => ({ value: k, label: v }))} />
          </Form.Item>
          <Form.Item name="action" label="处理方式" initialValue="replace">
            <Select options={Object.entries(actionLabels).map(([k, v]) => ({ value: k, label: v.label }))} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default SensitiveWordPage;
